/**
 * Shared e-mail validation for login and account e-mail change.
 * Rules mirror backend/EmailValidator.php — keep messages in sync.
 */

export const EMAIL_MAX_LENGTH = 254

export type EmailValidationResult =
  | { ok: true; email: string }
  | { ok: false; message: string }

export function validateEmail(raw: string): EmailValidationResult {
  const trimmed = raw.trim()
  if (trimmed === '') {
    return { ok: false, message: 'E-mail skal udfyldes.' }
  }
  if (trimmed.length > EMAIL_MAX_LENGTH) {
    return {
      ok: false,
      message: `E-mail er for lang (maks. ${EMAIL_MAX_LENGTH} tegn).`,
    }
  }
  if (/\s/.test(trimmed)) {
    return { ok: false, message: 'E-mail må ikke indeholde mellemrum.' }
  }

  const atCount = (trimmed.match(/@/g) ?? []).length
  if (atCount === 0) {
    return {
      ok: false,
      message: 'E-mail skal indeholde @ og et domæne (fx navn@firma.dk).',
    }
  }
  if (atCount > 1) {
    return { ok: false, message: 'E-mail må kun indeholde ét @.' }
  }

  const [local, domain] = trimmed.split('@')
  if (!local) {
    return { ok: false, message: 'Der skal stå noget før @.' }
  }
  if (local.startsWith('.') || local.endsWith('.')) {
    return {
      ok: false,
      message: 'Delen før @ må ikke starte eller slutte med punktum.',
    }
  }
  if (local.includes('..')) {
    return {
      ok: false,
      message: 'E-mail må ikke indeholde to punktummer i træk.',
    }
  }
  if (!domain) {
    return {
      ok: false,
      message: 'Der skal stå et domæne efter @ (fx firma.dk).',
    }
  }
  if (!domain.includes('.')) {
    return {
      ok: false,
      message: 'Domænet skal indeholde mindst ét punktum (fx .dk).',
    }
  }

  const email = trimmed.toLowerCase()
  // Same check PHP uses after structural rules.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return {
      ok: false,
      message:
        'E-mailadressen ser ikke gyldig ud. Tjek staving og domæne.',
    }
  }

  return { ok: true, email }
}
